import { computed, ref } from 'vue';
import { defineStore } from 'pinia';
import { z } from 'zod';
import type { FieldPermissionGrant, FormPermissionsStructure } from '@record-fill';

import { iterateFormElements } from '../lib/form-element-traversal';

import type { FormElement } from './form.types';
import { clonePlain } from './useLimitedHistory';

export const PERMISSIONS_STRUCTURE_VERSION = 1;

export interface PermissionsGrantsSnapshot {
  grantsByFieldId: Record<string, FieldPermissionGrant>;
  unrecognizedStructure: object | null;
}

const fieldPermissionSchema = z.object({
  fieldId: z.string().min(1),
  tableId: z.string().optional(),
  row: z.number().int().nonnegative().optional(),
  col: z.number().int().nonnegative().optional(),
  positionIds: z.array(z.number().int().positive()),
  functionalActivityIds: z.array(z.number().int().positive()),
});

export const permissionsStructureSchema = z.object({
  version: z.literal(PERMISSIONS_STRUCTURE_VERSION),
  fields: z.array(fieldPermissionSchema),
});

export type { FieldPermissionGrant, FormPermissionsStructure } from '@record-fill';

type Equals<A, B> = (<T>() => T extends A ? 1 : 2) extends <T>() => T extends B ? 1 : 2 ? true : false;
type AssertTrue<T extends true> = T;

// The schema validates what the builder persists and the lib type is what the engine reads in
// the browser and on the server: the build must break when one changes without the other.
export type PermissionsSchemaMatchesLibType = AssertTrue<
  Equals<z.infer<typeof permissionsStructureSchema>, FormPermissionsStructure>
>;
export type GrantSchemaMatchesLibType = AssertTrue<
  Equals<z.infer<typeof fieldPermissionSchema>, FieldPermissionGrant>
>;

export interface PermissionFieldTarget {
  fieldId: string;
  tableId?: string;
  row?: number;
  col?: number;
}

export interface PermissionSubjects {
  positionIds?: number[];
  functionalActivityIds?: number[];
}

interface LoadPersistedPermissionsOptions {
  permissionsStructure?: object | null;
}

function getPermissionsStorageKey(branchId: string | null): string | null {
  if (!branchId) return null;
  return `form-builder-permissions-draft-branch-${branchId}`;
}

function getEditPermissionsStorageKey(matrixId: number): string {
  return `form-builder-permissions-draft-matrix-${matrixId}`;
}

function normalizeIds(ids: number[] | undefined): number[] {
  return [...new Set(ids ?? [])].sort((a, b) => a - b);
}

function isEmptyGrant(grant: FieldPermissionGrant): boolean {
  return grant.positionIds.length === 0 && grant.functionalActivityIds.length === 0;
}

function compareFieldIds(a: string, b: string): number {
  if (a < b) return -1;
  return a > b ? 1 : 0;
}

function buildGrant(
  target: PermissionFieldTarget,
  subjects: PermissionSubjects
): FieldPermissionGrant {
  return {
    fieldId: target.fieldId,
    ...(target.tableId !== undefined ? { tableId: target.tableId } : {}),
    ...(target.row !== undefined ? { row: target.row } : {}),
    ...(target.col !== undefined ? { col: target.col } : {}),
    positionIds: normalizeIds(subjects.positionIds),
    functionalActivityIds: normalizeIds(subjects.functionalActivityIds),
  };
}

export const useFormPermissionsStore = defineStore('form-permissions', () => {
  const currentBranchId = ref<string | null>(localStorage.getItem('selectedBranchId'));
  const editPermissionsStorageKey = ref<string | null>(null);
  const localStorageSuspended = ref(false);
  const grantsByFieldId = ref<Record<string, FieldPermissionGrant>>({});
  const unrecognizedStructure = ref<object | null>(null);

  const configuredFieldIds = computed(() =>
    Object.values(grantsByFieldId.value)
      .filter((grant) => !isEmptyGrant(grant))
      .map((grant) => grant.fieldId)
  );

  const hasConfiguredPermissions = computed(() => configuredFieldIds.value.length > 0);

  function getActivePermissionsStorageKey(): string | null {
    return editPermissionsStorageKey.value ?? getPermissionsStorageKey(currentBranchId.value);
  }

  // An empty grant only arrives from a stored structure, since every setter deletes it instead.
  // It goes back as it came: dropping it would turn "nobody" into "everyone" on the next save.
  function buildPermissionsStructure(): FormPermissionsStructure {
    const fields = Object.values(grantsByFieldId.value)
      .map((grant) => ({
        ...grant,
        positionIds: normalizeIds(grant.positionIds),
        functionalActivityIds: normalizeIds(grant.functionalActivityIds),
      }))
      .sort((a, b) => compareFieldIds(a.fieldId, b.fieldId));

    return { version: PERMISSIONS_STRUCTURE_VERSION, fields };
  }

  function buildPersistablePermissionsStructure(): object | null {
    if (Object.keys(grantsByFieldId.value).length > 0) return buildPermissionsStructure();
    return unrecognizedStructure.value;
  }

  function persistPermissions() {
    if (localStorageSuspended.value) return;
    const key = getActivePermissionsStorageKey();
    if (!key) return;

    localStorage.setItem(key, JSON.stringify(buildPermissionsStructure()));
  }

  function getFieldPermission(fieldId: string): FieldPermissionGrant | null {
    return grantsByFieldId.value[fieldId] ?? null;
  }

  function isFieldPermissioned(fieldId: string): boolean {
    const grant = grantsByFieldId.value[fieldId];
    return grant !== undefined && !isEmptyGrant(grant);
  }

  function setFieldPermissions(target: PermissionFieldTarget, subjects: PermissionSubjects) {
    const grant = buildGrant(target, subjects);
    unrecognizedStructure.value = null;

    if (isEmptyGrant(grant)) {
      delete grantsByFieldId.value[target.fieldId];
    } else {
      grantsByFieldId.value[target.fieldId] = grant;
    }

    persistPermissions();
  }

  function applyToFields(targets: PermissionFieldTarget[], subjects: PermissionSubjects) {
    unrecognizedStructure.value = null;

    targets.forEach((target) => {
      const grant = buildGrant(target, subjects);

      if (isEmptyGrant(grant)) {
        delete grantsByFieldId.value[target.fieldId];
        return;
      }

      grantsByFieldId.value[target.fieldId] = grant;
    });

    persistPermissions();
  }

  function applySubjectsToFields(
    targets: PermissionFieldTarget[],
    key: keyof PermissionSubjects,
    ids: number[]
  ) {
    unrecognizedStructure.value = null;

    targets.forEach((target) => {
      const current = grantsByFieldId.value[target.fieldId];
      const grant = buildGrant(target, {
        positionIds: current?.positionIds,
        functionalActivityIds: current?.functionalActivityIds,
        [key]: ids,
      });

      if (isEmptyGrant(grant)) {
        delete grantsByFieldId.value[target.fieldId];
        return;
      }

      grantsByFieldId.value[target.fieldId] = grant;
    });

    persistPermissions();
  }

  function clearFieldPermissions(fieldId: string) {
    unrecognizedStructure.value = null;
    delete grantsByFieldId.value[fieldId];
    persistPermissions();
  }

  function clearPermissions() {
    unrecognizedStructure.value = null;
    grantsByFieldId.value = {};
    const key = getActivePermissionsStorageKey();
    if (key) localStorage.removeItem(key);
  }

  function reconcilePermissionsWithElements(elements: readonly FormElement[]): boolean {
    const existingFieldIds = new Set<string>();
    for (const element of iterateFormElements(elements, { includeTableContainers: true })) {
      existingFieldIds.add(element.id);
    }

    const orphanFieldIds = Object.keys(grantsByFieldId.value).filter(
      (fieldId) => !existingFieldIds.has(fieldId)
    );
    if (orphanFieldIds.length === 0) return false;

    orphanFieldIds.forEach((fieldId) => {
      delete grantsByFieldId.value[fieldId];
    });
    persistPermissions();
    return true;
  }

  function applyPermissionsStructure(structure: FormPermissionsStructure) {
    unrecognizedStructure.value = null;
    grantsByFieldId.value = structure.fields.reduce<Record<string, FieldPermissionGrant>>(
      (accumulator, grant) => {
        accumulator[grant.fieldId] = grant;
        return accumulator;
      },
      {}
    );
  }

  function parseStoredPermissions(raw: string): FormPermissionsStructure | null {
    try {
      const parsed = permissionsStructureSchema.safeParse(JSON.parse(raw));
      return parsed.success ? parsed.data : null;
    } catch {
      return null;
    }
  }

  function getStoredPermissionsDraft(key: string): FormPermissionsStructure | null {
    const stored = localStorage.getItem(key);
    if (!stored) return null;

    const draft = parseStoredPermissions(stored);
    if (draft) return draft;

    localStorage.removeItem(key);
    return null;
  }

  function captureGrants(): PermissionsGrantsSnapshot {
    return {
      grantsByFieldId: clonePlain(grantsByFieldId.value),
      unrecognizedStructure: clonePlain(unrecognizedStructure.value),
    };
  }

  function restoreGrants(snapshot: PermissionsGrantsSnapshot) {
    grantsByFieldId.value = clonePlain(snapshot.grantsByFieldId);
    unrecognizedStructure.value = clonePlain(snapshot.unrecognizedStructure);
    persistPermissions();
  }

  function reset() {
    grantsByFieldId.value = {};
    unrecognizedStructure.value = null;
  }

  function loadPersistedPermissions(options: LoadPersistedPermissionsOptions = {}) {
    const key = getActivePermissionsStorageKey();
    reset();

    const parsed = permissionsStructureSchema.safeParse(options.permissionsStructure);
    if (parsed.success) {
      applyPermissionsStructure(parsed.data);
      return;
    }

    if (options.permissionsStructure) {
      unrecognizedStructure.value = options.permissionsStructure;
      return;
    }

    if (key) {
      const storedDraft = getStoredPermissionsDraft(key);
      if (storedDraft) applyPermissionsStructure(storedDraft);
    }
  }

  function initForCreateMode() {
    currentBranchId.value = localStorage.getItem('selectedBranchId');
    editPermissionsStorageKey.value = null;
    localStorageSuspended.value = false;
    loadPersistedPermissions();
  }

  function setEditDraftStorage(matrixId: number) {
    currentBranchId.value = localStorage.getItem('selectedBranchId');
    editPermissionsStorageKey.value = getEditPermissionsStorageKey(matrixId);
    localStorage.removeItem(editPermissionsStorageKey.value);
    localStorageSuspended.value = true;
  }

  function getSelectionStorageKey(): string | null {
    if (localStorageSuspended.value) return null;
    const key = getActivePermissionsStorageKey();
    return key ? `${key}-selection` : null;
  }

  function clearDraft() {
    const key = getActivePermissionsStorageKey();
    if (key) {
      localStorage.removeItem(key);
      localStorage.removeItem(`${key}-selection`);
    }
  }

  function reloadForCurrentBranch() {
    if (localStorageSuspended.value || editPermissionsStorageKey.value) return;

    const newBranchId = localStorage.getItem('selectedBranchId');
    if (newBranchId === currentBranchId.value) return;

    currentBranchId.value = newBranchId;
    loadPersistedPermissions();
  }

  return {
    grantsByFieldId,
    configuredFieldIds,
    hasConfiguredPermissions,
    unrecognizedStructure,
    getFieldPermission,
    isFieldPermissioned,
    setFieldPermissions,
    applyToFields,
    applySubjectsToFields,
    clearFieldPermissions,
    clearPermissions,
    captureGrants,
    restoreGrants,
    reconcilePermissionsWithElements,
    buildPermissionsStructure,
    buildPersistablePermissionsStructure,
    loadPersistedPermissions,
    initForCreateMode,
    setEditDraftStorage,
    clearDraft,
    getSelectionStorageKey,
    reloadForCurrentBranch,
    reset,
  };
});
